import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  partnerStatusList,
  partnerStatusObject,
} from "../../../components/utils/allProductStatus";
import MobileSearchBar from "../../ui/MobileSearchBar";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileList from "../../ui/MobileList";
import MobileListItem from "../../ui/MobileListItem";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileError from "../../ui/MobileError";
import { usePartnerProductsSearch } from "./usePartnerProductsSearch";

function formatStatusLabel(statuses) {
  if (!statuses?.length) {
    return "Todos";
  }
  if (statuses.length === 1) {
    return partnerStatusObject[statuses[0]] ?? statuses[0];
  }
  return `${statuses.length} status`;
}

export default function MobilePartnerProductsList({ user }) {
  const navigate = useNavigate();
  const { products, loading, error, filters, setFilters, search, refresh } =
    usePartnerProductsSearch(user);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(filters);

  useEffect(() => {
    search();
  }, [search]);

  const openFilters = () => {
    setDraftFilters(filters);
    setFiltersOpen(true);
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    setFiltersOpen(false);
    search(draftFilters);
  };

  const handleStatusToggle = (status) => {
    setDraftFilters((current) => {
      const selected = current.status ?? [];
      if (selected.includes(status)) {
        return { ...current, status: selected.filter((item) => item !== status) };
      }
      return { ...current, status: [...selected, status] };
    });
  };

  const handleCreate = () => {
    navigate("/partner/products/create");
  };

  const activeFilterCount =
    (filters.id ? 1 : 0) +
    (filters.status?.length ? 1 : 0);

  if (!user?.store) {
    return (
      <div className="px-4 py-8 text-center text-laranjinha/70">
        Você não possui um reino parceiro
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button
          type="button"
          onClick={handleCreate}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-primary px-3 text-sm font-medium text-mgs-bg hover:bg-[#b47c06]"
        >
          Criar
        </button>
        <button
          type="button"
          onClick={refresh}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-primary/50 px-3 text-sm font-medium text-laranjinha/80 hover:bg-monao"
        >
          Atualizar
        </button>
      </div>

      <MobileSearchBar
        value={filters.q}
        onChange={(q) => setFilters((current) => ({ ...current, q }))}
        onSubmit={(q) => {
          const next = { ...filters, q: typeof q === "string" ? q : "" };
          setFilters(next);
          search(next);
        }}
        placeholder="Nome, CPF, e-mail ou rastreio"
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button
          type="button"
          onClick={openFilters}
          className="inline-flex min-h-[36px] items-center rounded-full border border-primary/50 bg-monao px-3 text-sm text-laranjinha/80 hover:bg-monao"
        >
          Filtros{activeFilterCount ? ` (${activeFilterCount})` : ""}
        </button>
        <span className="inline-flex min-h-[36px] items-center rounded-full bg-primary/15 px-3 text-sm text-primary">
          Status: {formatStatusLabel(filters.status)}
        </span>
      </div>

      {loading ? (
        <div aria-label="Carregando pedidos" role="status">
          <MobileSkeleton lines={5} />
        </div>
      ) : error ? (
        <MobileError
          message={error.message ?? "Erro ao buscar pedidos."}
          onRetry={refresh}
        />
      ) : products.length === 0 ? (
        <MobileEmpty
          title="Nenhum pedido encontrado"
          message="Ajuste os filtros ou tente atualizar a lista."
        />
      ) : (
        <MobileList>
          {products.map((product) => (
            <MobileListItem
              key={product._id}
              title={product.user?.name ?? "Sem nome"}
              subtitle={partnerStatusObject[product.status] ?? product.status}
              onClick={() => navigate(`/partner/products/${product._id}`)}
            />
          ))}
        </MobileList>
      )}

      <MobileFilterSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filtros"
      >
        <div className="space-y-4">
          <label className="block text-sm font-medium text-laranjinha/80">
            ID do pedido
            <input
              type="text"
              value={draftFilters.id}
              onChange={(e) =>
                setDraftFilters((current) => ({ ...current, id: e.target.value }))
              }
              className="mt-1 w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha"
            />
          </label>

          <fieldset>
            <legend className="text-sm font-medium text-laranjinha/80">Status</legend>
            <div className="mt-2 max-h-48 space-y-2 overflow-y-auto">
              {partnerStatusList.map((status) => (
                <label
                  key={status}
                  className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-2 hover:bg-monao"
                >
                  <input
                    type="checkbox"
                    checked={draftFilters.status?.includes(status) ?? false}
                    onChange={() => handleStatusToggle(status)}
                    className="h-5 w-5 rounded border-primary/50"
                  />
                  <span className="text-sm text-laranjinha">
                    {partnerStatusObject[status]}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <button
            type="button"
            onClick={applyFilters}
            className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary px-4 font-medium text-mgs-bg hover:bg-[#b47c06]"
          >
            Aplicar filtros
          </button>
        </div>
      </MobileFilterSheet>
    </div>
  );
}
